/**
 * Cart service for Custom Backend operations
 * Handles all cart-related API calls
 */

import { BaseCustomService } from "../services/base";
import {
  ICartItemRequest,
  ICartUpdateRequest,
  IOptionsRequest,
  IResponse,
} from "../../../models";
import { formatResponse } from "../../../core/utils";
import { ICustomConfig } from "../interface";

export class CartService extends BaseCustomService {
  constructor(baseUrl: string, config: ICustomConfig) {
    super(baseUrl, config);
  }

  /**
   * Create a new cart
   */
  async createCart(options?: IOptionsRequest): Promise<IResponse> {
    try {
      const result = await this.request("/cart", "GET", undefined, options);

      return formatResponse({
        success: true,
        message: "Cart created successfully.",
        data: result,
        meta: undefined,
      });
    } catch (error: any) {
      this.logger.error(
        "Failed to create cart",
        error instanceof Error ? error : new Error(String(error))
      );

      return formatResponse({
        success: false,
        message: "Failed to create cart.",
        data: null,
        error: {
          message: error instanceof Error ? error.message : String(error),
          details: error,
        },
      });
    }
  }

  /**
   * Get a cart by ID or current cart
   */
  async getCart(id?: string, options?: IOptionsRequest): Promise<IResponse> {
    try {
      const result = await this.request("/cart", "GET", undefined, options);

      return formatResponse({
        success: true,
        message: "Cart retrieved successfully.",
        data: result,
        meta: undefined,
      });
    } catch (error: any) {
      this.logger.error(
        "Failed to get cart",
        error instanceof Error ? error : new Error(String(error))
      );

      return formatResponse({
        success: false,
        message: "Failed to retrieve cart.",
        data: null,
        error: {
          message: error instanceof Error ? error.message : String(error),
          details: error,
        },
      });
    }
  }

  /**
   * Update a cart
   * For custom backend, this handles both full cart updates and individual item quantity changes
   */
  async updateCart(
    id: string,
    payload: ICartUpdateRequest,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    try {
      let result;

      // Check if this is a single item change request
      if (payload && Array.isArray(payload.items)) {
        // For custom backend, use changeCart for individual item updates
        const promises = payload.items.map((item: any) => {
          const formattedPayload = {
            variant_id: item.variantId || item.id,
            quantity: item.quantity,
          };

          return this.request(
            "/cart/change",
            "POST",
            formattedPayload,
            options
          );
        });

        // Execute all changes and return the last cart state
        const results = await Promise.all(promises);
        result = results[results.length - 1];
      }
      // For other types of updates, use the standard update endpoint
      else {
        result = await this.request("/cart/update", "POST", payload, options);
      }

      return formatResponse({
        success: true,
        message: "Cart updated successfully.",
        data: result,
        meta: undefined,
      });
    } catch (error: any) {
      this.logger.error(
        "Failed to update cart",
        error instanceof Error ? error : new Error(String(error))
      );

      return formatResponse({
        success: false,
        message: "Failed to update cart.",
        data: null,
        error: {
          message: error instanceof Error ? error.message : String(error),
          details: error,
        },
      });
    }
  }

  /**
   * Add items to a cart
   */
  async addToCart(
    id: string,
    items: ICartItemRequest[],
    options?: IOptionsRequest
  ): Promise<IResponse> {
    try {
      if (!items || items.length === 0) {
        return formatResponse({
          success: false,
          message: "No items provided to add to cart.",
        });
      }

      const payload = {
        items: items.map((item) => ({
          id: item.variantId,
          quantity: item.quantity,
        })),
      };

      const result = await this.request("/cart/add", "POST", payload, options);

      return formatResponse({
        success: true,
        message: "Items added to cart successfully.",
        data: result,
        meta: undefined,
      });
    } catch (error: any) {
      this.logger.error(
        "Failed to add items to cart",
        error instanceof Error ? error : new Error(String(error))
      );

      return formatResponse({
        success: false,
        message: "Failed to add items to cart.",
        data: null,
        error: {
          message: error instanceof Error ? error.message : String(error),
          details: error,
        },
      });
    }
  }

  /**
   * Remove items from a cart
   */
  async removeFromCart(
    id: string,
    lineIds: string[],
    options?: IOptionsRequest
  ): Promise<IResponse> {
    try {
      if (!lineIds || lineIds.length === 0) {
        return formatResponse({
          success: false,
          message: "No line IDs provided to remove from cart.",
        });
      }

      const promises = lineIds.map((lineId) =>
        this.request(
          "/cart/change",
          "POST",
          {
            variant_id: lineId,
            quantity: 0,
          },
          options
        )
      );

      // Execute all removals and return the last cart state
      const results = await Promise.all(promises);
      const result = results[results.length - 1];

      return formatResponse({
        success: true,
        message: "Items removed from cart successfully.",
        data: result,
        meta: undefined,
      });
    } catch (error: any) {
      this.logger.error(
        "Failed to remove items from cart",
        error instanceof Error ? error : new Error(String(error))
      );

      return formatResponse({
        success: false,
        message: "Failed to remove items from cart.",
        data: null,
        error: {
          message: error instanceof Error ? error.message : String(error),
          details: error,
        },
      });
    }
  }

  /**
   * Clear all items from the cart
   */
  async clearCart(
    payload: any = {},
    options?: IOptionsRequest
  ): Promise<IResponse> {
    try {
      const result = await this.request(
        "/cart/clear",
        "POST",
        payload,
        options
      );

      return formatResponse({
        success: true,
        message: "Cart cleared successfully.",
        data: result,
        meta: undefined,
      });
    } catch (error: any) {
      this.logger.error(
        "Failed to clear cart",
        error instanceof Error ? error : new Error(String(error))
      );

      return formatResponse({
        success: false,
        message: "Failed to clear cart.",
        data: null,
        error: {
          message: error instanceof Error ? error.message : String(error),
          details: error,
        },
      });
    }
  }

  /**
   * Create checkout
   */
  async createCheckout(
    payload: any = {},
    options?: IOptionsRequest
  ): Promise<IResponse> {
    try {
      const result = await this.request("/checkout", "POST", payload, options);

      return formatResponse({
        success: true,
        message: "Checkout created successfully.",
        data: result,
        meta: undefined,
      });
    } catch (error: any) {
      this.logger.error(
        "Failed to create checkout",
        error instanceof Error ? error : new Error(String(error))
      );

      return formatResponse({
        success: false,
        message: "Failed to create checkout.",
        data: null,
        error: {
          message: error instanceof Error ? error.message : String(error),
          details: error,
        },
      });
    }
  }
}
